import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";

interface AppStoreDownloadProps {
  headline: string;
  description?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
  qrCodeImage?: string;
}

export function AppStoreDownload({
  headline,
  description,
  appStoreUrl,
  playStoreUrl,
  qrCodeImage,
}: AppStoreDownloadProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            "text-center max-w-3xl mx-auto",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Mobile App</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            <GradientText>{headline}</GradientText>
          </h2>

          {description && (
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-10">
              {description}
            </p>
          )}

          {/* Store badges */}
          <div
            className={cn(
              "flex flex-wrap items-center justify-center gap-4 mb-10",
              "transition-all duration-500",
              isVisible ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
          >
            {appStoreUrl && (
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <svg
                  width="135"
                  height="40"
                  viewBox="0 0 135 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-auto"
                >
                  <rect width="135" height="40" rx="5" fill="black" />
                  <rect
                    x="0.5"
                    y="0.5"
                    width="134"
                    height="39"
                    rx="4.5"
                    stroke="white"
                    strokeOpacity="0.2"
                  />
                  <text
                    x="52"
                    y="14"
                    fill="white"
                    fontSize="8"
                    fontFamily="system-ui"
                  >
                    Download on the
                  </text>
                  <text
                    x="52"
                    y="28"
                    fill="white"
                    fontSize="16"
                    fontFamily="system-ui"
                    fontWeight="500"
                  >
                    App Store
                  </text>
                  <path
                    d="M27.5 20.5C27.5 16.08 31.08 12.5 35.5 12.5C33.5 10.5 30.5 9 27 9C23 9 19.5 11.5 18 15C16.5 11.5 13 9 9 9C4.03 9 0 13.03 0 18C0 27 13.5 36 18 36C22.5 36 36 27 36 18C36 13.03 31.97 9 27 9"
                    transform="translate(12, 6) scale(0.5)"
                    fill="white"
                  />
                  <path
                    d="M22 8C22 8 23.5 6 24.5 5C25.5 4 28 3 28 3C28 3 27 5.5 26 7C25 8.5 22 10 22 10C22 10 22 8 22 8Z"
                    transform="translate(12, 6) scale(0.5)"
                    fill="white"
                  />
                </svg>
              </a>
            )}

            {playStoreUrl && (
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <svg
                  width="135"
                  height="40"
                  viewBox="0 0 135 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-auto"
                >
                  <rect width="135" height="40" rx="5" fill="black" />
                  <rect
                    x="0.5"
                    y="0.5"
                    width="134"
                    height="39"
                    rx="4.5"
                    stroke="white"
                    strokeOpacity="0.2"
                  />
                  <text
                    x="48"
                    y="14"
                    fill="white"
                    fontSize="8"
                    fontFamily="system-ui"
                  >
                    GET IT ON
                  </text>
                  <text
                    x="48"
                    y="28"
                    fill="white"
                    fontSize="14"
                    fontFamily="system-ui"
                    fontWeight="500"
                  >
                    Google Play
                  </text>
                  <path
                    d="M12 4L24 12L12 20V4Z"
                    transform="translate(12, 10)"
                    fill="#34A853"
                  />
                  <path
                    d="M12 4L24 12L20 16L12 12V4Z"
                    transform="translate(12, 10)"
                    fill="#FBBC04"
                  />
                  <path
                    d="M12 12L20 16L24 12L12 20V12Z"
                    transform="translate(12, 10)"
                    fill="#EA4335"
                  />
                  <path
                    d="M12 4V12L20 8L12 4Z"
                    transform="translate(12, 10)"
                    fill="#4285F4"
                  />
                </svg>
              </a>
            )}
          </div>

          {/* QR Code */}
          {qrCodeImage && (
            <div
              className={cn(
                "inline-flex flex-col items-center gap-3",
                "transition-all duration-500",
                isVisible ? "opacity-100" : "opacity-0"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
            >
              <div
                className={cn(
                  "w-32 h-32 rounded-xl overflow-hidden",
                  "bg-card p-2 border border-border"
                )}
              >
                <img
                  src={qrCodeImage}
                  alt="Scan to download"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.parentElement!.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs text-center p-2">
                        QR Code
                      </div>
                    `;
                  }}
                />
              </div>
              <span className="text-muted-foreground text-sm">Scan to download</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
